import { FormHandler } from '@droplet-tech-code/core-elements/module/ui/Inputs/Form';
import {
  GetPurchaseResponse,
  PurchaseGetCancelCalculationResponse,
  SupportRequestBackendClient,
} from '@naus-code/naus-admin-types';
import { MutableRefObject } from 'react';
import { createGlobalState } from 'react-hooks-global-state';

export const ModalActionPanelCancelKey = 'ActionPanelCancel';

export interface CancelPurchaseProps {
  purchaseId: string;
  supportRequest: SupportRequestBackendClient;
  purchase: GetPurchaseResponse['purchase'];
}

export interface CancelPurchasePropsNative {
  purchaseId: string;
  supportRequest: SupportRequestBackendClient;
  purchase: GetPurchaseResponse['purchase'];
  checkedItems: string[];
  setCheckedItems: (checkedItems: string[]) => void;
}

export interface CancelPurchaseFooterPropsNative {
  purchaseId: string;
  supportRequest: SupportRequestBackendClient;
  purchase: GetPurchaseResponse['purchase'];
  currentPage: number;
  setCurrentPage: (number) => void;
}

export type CancellationMethod =
  | 'visual'
  | 'customer'
  | 'provider'
  | 'customer_and_provider';

class CancelPurchaseManager {
  state = this._assignState();
  private _assignState() {
    return createGlobalState<{
      type: CancellationMethod | undefined;
      selectedTickets: string[];
    }>({
      type: 'customer_and_provider',
      selectedTickets: [],
    });
  }
  reset() {
    this.state = this._assignState();
  }
}

export interface CancelPurchaseForm {
  formHandlerRef: MutableRefObject<FormHandler>;
  purchaseCalculation?: PurchaseGetCancelCalculationResponse;
  supportRequest?: SupportRequestBackendClient;
}

export const cancelPurchaseManager = new CancelPurchaseManager();

export const getCancelRefundType = (
  supportRequest: SupportRequestBackendClient | undefined,
) => {
  if (
    supportRequest?.requestOptions?.type === 'cancel' ||
    supportRequest?.requestOptions?.type === 'cancel_v2'
  ) {
    const method = supportRequest?.requestOptions?.refundMethod;

    if (method?.type === 'voucher') {
      return 'voucher' as const;
    }
    if (method?.type === 'bank') {
      return 'bank' as const;
    }
  }
};

// The cancel forms collect company-refund amounts in the company's currency,
// but customerOptions.expectedRefundFromCompanyByPurchase must reach the server
// in the customer's (booking) currency — only providerOptions stays in company ccy.
// Rather than convert via an exchange rate, derive the refund as a percentage
// of the purchase's company-ccy value and apply that percentage to the
// purchase's customer-ccy value (same approach used to derive customerRefundInBookCcy
// from an actual-refund entry in CancelBooking.Form).
export const convertCompanyCcyToCustomerCcy = (
  value: number | undefined,
  purchase: Pick<
    GetPurchaseResponse['purchase'],
    'ccy' | 'companyCcy' | 'valueCcy' | 'valueCompanyCcy'
  >,
): number => {
  if (!value || !isFinite(value)) {
    return 0;
  }
  if (purchase.ccy === purchase.companyCcy) {
    return value;
  }
  if (!purchase.valueCompanyCcy) {
    return value;
  }
  const refundPercentage = value / purchase.valueCompanyCcy;
  return Math.round(refundPercentage * purchase.valueCcy);
};

export const getServiceFee = (
  refundMethod: 'bank' | 'voucher' | undefined,
  props: CancelPurchaseForm,
) => {
  if (refundMethod === 'bank') {
    return props.purchaseCalculation?.serviceRefundFee;
  }

  if (refundMethod === 'voucher') {
    return props.purchaseCalculation?.serviceFeeVoucher;
  }
};
